import { useMemo } from "react";
import { useLayerStale } from "../../hooks/useLayerStale";
import { DataChangeFeature } from "../../types/dataReview";

/** Colours by what a change would become. */
export const TARGET_COLOURS: Record<string, string> = {
  Lawn: "#8fd35b",
  Cleared: "#9aa3ad",
  Unchanged: "#6b7280",
  Water: "#2b6cb0",
  Tree: "#1f7a3d",
  Shrub: "#5bb36a",
  Building: "#c9a66b",
};
const DEFAULT_COLOUR = "#b35bd1";

// The project's own class colours win over the built-in ones, as in the
// editor's panels and the vegetation tiles.
const colourExpression = (extra: Record<string, string> = {}) => {
  const match: any[] = ["match", ["get", "target"]];
  for (const [k, v] of Object.entries({ ...TARGET_COLOURS, ...extra })) match.push(k, v);
  match.push(DEFAULT_COLOUR);
  return match;
};

/** Proposed changes on the map: dashed while undecided, solid green once
 *  accepted, faint red once rejected, bright when it is the one on the
 *  review card. Clicking one puts it on the card. */
const DataReviewLayer = (
  id: string,
  features: DataChangeFeature[],
  currentId: number | null,
  onPick: (id: number) => void,
  visible = true,
  /** Shapes picked together (a lasso), outlined as a group. */
  selectedIds: number[] = [],
  /** The project's own class colours, so painted ground matches the tiles. */
  colours: Record<string, string> = {},
) => {
  const selectedKey = selectedIds.join(",");
  const source = useMemo(
    () => {
      const selected = new Set(selectedIds);
      return {
        type: "FeatureCollection",
        features: features
          // A rejected change is gone as far as the map is concerned, unless
          // it is the one on the card (so a mistaken reject can be undone).
          .filter((f) => f.properties.status !== "rejected" || f.properties.id === currentId)
          .map((f) => ({
            ...f,
            properties: {
              ...f.properties, current: f.properties.id === currentId, selected: selected.has(f.properties.id),
              painted: !!f.properties.attrs?.painted,
            },
          })),
      };
    },
    [features, currentId, selectedKey],
  );
  const colourKey = Object.entries(colours).map(([k, v]) => `${k}=${v}`).join("|");
  const colour = useMemo(() => colourExpression(colours), [colourKey]);
  const staleOf = useLayerStale();
  const fillLayer = {
    id,
    type: "fill" as const,
    paint: {
      "fill-color": colour,
      "fill-opacity": [
        "case",
        ["boolean", ["get", "current"], false], 0.8,
        ["boolean", ["get", "painted"], false], 0.7,
        ["==", ["get", "status"], "accepted"], 0.55,
        ["==", ["get", "status"], "rejected"], 0.08,
        0.35,
      ],
    },
  };
  const pendingLine = {
    id: `${id}-pending`,
    type: "line" as const,
    filter: ["all", ["==", ["get", "status"], "pending"], ["!", ["boolean", ["get", "current"], false]]],
    paint: { "line-color": colour, "line-width": 1.5, "line-dasharray": [2, 1.5] },
  };
  const decidedLine = {
    id: `${id}-decided`,
    type: "line" as const,
    filter: ["all", ["!=", ["get", "status"], "pending"], ["!", ["boolean", ["get", "current"], false]]],
    paint: {
      "line-color": ["case", ["==", ["get", "status"], "accepted"], "#1b7f3b", "#d9534f"],
      "line-width": ["case", ["==", ["get", "status"], "accepted"], 2, 1.2],
    },
  };
  const currentLine = {
    id: `${id}-current`,
    type: "line" as const,
    filter: ["boolean", ["get", "current"], false],
    paint: { "line-color": "#ffffff", "line-width": 3 },
  };
  const selectedLine = {
    id: `${id}-selected`,
    type: "line" as const,
    filter: ["all", ["boolean", ["get", "selected"], false], ["!", ["boolean", ["get", "current"], false]]],
    paint: { "line-color": "#60d1af", "line-width": 2.5 },
  };
  return {
    id,
    name: "Proposed changes",
    type: "data-review",
    source,
    layerStyle: [fillLayer, pendingLine, decidedLine, selectedLine, currentLine],
    styleKey: colourKey,
    toolbarButton: null,
    active: visible,
    ...staleOf(id, source, colourKey),
    interactiveLayerIds: [id],
    onClick: (e: any) => {
      const f = e?.features?.[0];
      const picked = f?.properties?.id;
      if (picked === undefined || picked === null) return false;
      onPick(Number(picked));
      return true;
    },
  };
};

export default DataReviewLayer;
